import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api, formatEmployeeName, detectTitlePosition, ROLE_OPTIONS, ORG_UNIT_OPTIONS, ROLE_LABEL, downloadAsBlob } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Scan, X as XIcon, CheckCircle2, Printer } from "lucide-react";

const DEFAULT_VACATION_DAYS = 30;
const MAX_DAYS_PER_YEAR = 365;
const ENROLL_WINDOW_SECONDS = 30;

/**
 * Iter57f — Self-enroll RFID scanner widget.
 * Admin clicks "Skeniraj karticu", backend opens a 30s window, first unknown
 * scan gets auto-mapped to this employee. We poll /status every 1s.
 */
function RfidScanButton({ employeeEmail, currentUid, onScanned, disabled }) {
  const [active, setActive] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  const stop = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setActive(false);
    setSecondsLeft(0);
  };

  useEffect(() => () => stop(), []);  // cleanup on unmount

  const start = async () => {
    if (!employeeEmail || employeeEmail === "new") {
      toast.error("Prvo spremi zaposlenika, pa onda skeniraj karticu.");
      return;
    }
    try {
      const r = await api.post(`/users/${encodeURIComponent(employeeEmail)}/rfid-enroll/start`);
      const expiresAt = new Date(r.data.expires_at).getTime();
      setActive(true);
      setSecondsLeft(ENROLL_WINDOW_SECONDS);
      toast.info("Prinesite karticu Pi čitaču u sljedećih 30 sekundi…");

      // Countdown UI
      tickRef.current = setInterval(() => {
        const rem = Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
        setSecondsLeft(rem);
      }, 1000);

      // Polling status
      pollRef.current = setInterval(async () => {
        try {
          const s = await api.get(`/users/${encodeURIComponent(employeeEmail)}/rfid-enroll/status`);
          const st = s.data.status;
          if (st === "completed") {
            stop();
            onScanned(s.data.card_uid);
            toast.success(`Kartica skenirana: ${s.data.card_uid}`);
          } else if (st === "failed") {
            stop();
            toast.error(s.data.error || "Skeniranje nije uspjelo.");
          } else if (st === "expired") {
            stop();
            toast.warning("Vrijeme isteklo — kartica nije prinesena u 30 sekundi.");
          } else if (st === "cancelled") {
            stop();
          }
        } catch (e) {
          // ne-fatal — polling se nastavlja (transient network error tolerated)
          console.debug("[RfidScanButton] poll error, retrying:", e?.message);
        }
      }, 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška pri pokretanju skeniranja");
    }
  };

  const cancel = async () => {
    try {
      await api.delete(`/users/${encodeURIComponent(employeeEmail)}/rfid-enroll`);
    } catch (e) {
      // non-fatal — user explicitly asked to cancel; server-side timeout will clean up anyway
      console.debug("[RfidScanButton] cancel API failed (non-fatal):", e?.message);
    }
    stop();
    toast.info("Skeniranje otkazano.");
  };

  if (active) {
    return (
      <div className="flex items-center gap-2" data-testid="rfid-scan-active">
        <Button type="button" variant="destructive" size="sm" onClick={cancel} data-testid="rfid-scan-cancel">
          <XIcon className="w-3.5 h-3.5 mr-1"/>
          Odustani ({secondsLeft}s)
        </Button>
        <span className="text-xs text-muted-foreground animate-pulse">
          Čekam scan…
        </span>
      </div>
    );
  }
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={start}
      disabled={disabled}
      data-testid="rfid-scan-start"
      title={currentUid ? `Trenutno: ${currentUid}` : "Aktivirajte 30s prozor — sljedeći scan se automatski mapira"}
    >
      <Scan className="w-3.5 h-3.5 mr-1"/>
      {currentUid ? "Skeniraj novu karticu" : "Skeniraj karticu"}
    </Button>
  );
}

/**
 * Create / edit employee dialog. Heavy form extracted from Employees.jsx.
 * Parent owns form state and managerCandidates / customRoles / workSchedules lists.
 */
export function EmployeeEditDialog({
  open, onClose, form, setForm, onSave,
  managerCandidates = [],
  customRoles = [],
  workSchedules = [],
  isFullAdmin = false,
}) {
  // Iter70: prijenosna godina iz Postavki (carryover_prev_year; 0 = automatski tekuća-1)
  const [prevYear, setPrevYear] = useState(new Date().getFullYear() - 1);
  useEffect(() => {
    api.get("/settings").then(r => {
      const y = Number(r.data?.carryover_prev_year || 0);
      if (y >= 2000) setPrevYear(y);
    }).catch(() => {});
  }, []);
  if (!open) return null;
  return (
    <Dialog open={!!open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle>{open === "new" ? "Novi zaposlenik" : "Uredi zaposlenika"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 px-6 pb-4 overflow-y-auto flex-1" data-testid="emp-form-scroll">
          <div className="space-y-1"><Label>Ime i prezime *</Label>
            <Input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} data-testid="emp-name"/>
          </div>
          <div className="space-y-1">
            <Label>Akademsko zvanje *</Label>
            <Input value={form.academic_title} onChange={e => setForm({...form, academic_title: e.target.value})}
              placeholder="npr. prof. dr. sc., mag. ing. tech. inf., prof."
              required data-testid="emp-academic-title"/>
          </div>
          <div className="space-y-1 col-span-2 bg-secondary/30 rounded p-2 -mt-1">
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Pravilo formatiranja na rješenju — automatski: ako naziv sadrži <code className="bg-background px-1 rounded">dr.</code> stavlja se <b>ispred</b> imena (npr. <i>prof. dr. sc. Ivan Horvat</i>); inače se stavlja <b>iza</b> imena sa zarezom (npr. <i>Ivan Horvat, mag. ing. tech. inf.</i>).
            </p>
            {form.full_name?.trim() && form.academic_title?.trim() && (
              <p className="text-xs mt-1">Prikaz: <b data-testid="emp-name-preview">{formatEmployeeName(form)}</b> <span className="text-[10px] text-muted-foreground">({detectTitlePosition(form.academic_title) === "prefix" ? "ispred" : "iza"})</span></p>
            )}
          </div>
          <div className="space-y-1 col-span-2">
            <Label>E-mail (ostavite prazno za interne korisnike bez maila)</Label>
            <Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} disabled={open !== "new" && !isFullAdmin} data-testid="emp-email"/>
          </div>
          <div className="space-y-1"><Label>Naziv jedinice (Katedra / Odjel / ...)</Label>
            <Input value={form.katedra} onChange={e => setForm({...form, katedra: e.target.value})} data-testid="emp-katedra"/>
          </div>
          <div className="space-y-1"><Label>Vrsta org. jedinice</Label>
            <Select
              value={form.organizacijska_jedinica_tip || "katedra"}
              onValueChange={v => setForm({...form, organizacijska_jedinica_tip: v})}
            >
              <SelectTrigger data-testid="emp-org-unit-tip"><SelectValue/></SelectTrigger>
              <SelectContent>
                {ORG_UNIT_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 col-span-2"><Label>Radno mjesto</Label>
            <Input value={form.radno_mjesto} onChange={e => setForm({...form, radno_mjesto: e.target.value})} data-testid="emp-position"/>
          </div>
          <div className="space-y-1 col-span-2">
            <Label>Nadređeni (e-mail) {!isFullAdmin && <span className="text-xs text-muted-foreground">— samo tajništvo može mijenjati</span>}</Label>
            <Select value={form.manager_email || ""} onValueChange={v => setForm({...form, manager_email: v})} disabled={!isFullAdmin}>
              <SelectTrigger data-testid="emp-manager"><SelectValue placeholder="Odaberi nadređenog"/></SelectTrigger>
              <SelectContent className="max-h-72">
                {managerCandidates.map(m => (
                  <SelectItem key={m.email} value={m.email}>
                    <span className="inline-flex items-center gap-2">
                      <span>{m.full_name}</span>
                      <span className="text-[10px] uppercase tracking-wider bg-secondary text-secondary-foreground rounded px-1.5 py-0.5">
                        {ROLE_LABEL[m.role] || m.role}
                      </span>
                      <span className="text-xs text-muted-foreground">({m.email})</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isFullAdmin && <div className="space-y-1 col-span-2"><Label>Uloga</Label>
            <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
              <SelectTrigger data-testid="emp-role"><SelectValue/></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select></div>}
          {isFullAdmin && customRoles.length > 0 && (
            <div className="space-y-1 col-span-2">
              <Label>
                Custom uloga (opcionalno)
                <span className="text-xs text-muted-foreground ml-2">— ako dodijelite, bazna uloga se automatski postavlja</span>
              </Label>
              <Select
                value={form.custom_role_id || "__none__"}
                onValueChange={v => {
                  if (v === "__none__") {
                    setForm({ ...form, custom_role_id: "" });
                  } else {
                    const cr = customRoles.find(c => c.id === v);
                    setForm({ ...form, custom_role_id: v, role: cr?.base_role || form.role });
                  }
                }}>
                <SelectTrigger data-testid="emp-custom-role"><SelectValue placeholder="Bez custom uloge"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Bez custom uloge —</SelectItem>
                  {customRoles.map(cr => (
                    <SelectItem key={cr.id} value={cr.id}>
                      {cr.name} <span className="text-xs text-muted-foreground ml-1">(baza: {cr.base_role})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {isFullAdmin && <>
            <div className="space-y-1">
              <Label>Ukupni fond dana godišnjeg odmora</Label>
              <Input type="number" min={0} max={MAX_DAYS_PER_YEAR} value={form.ukupni_fond_dana ?? DEFAULT_VACATION_DAYS}
                onChange={e => setForm({...form, ukupni_fond_dana: e.target.value})}
                data-testid="emp-fond-dana"/>
              <p className="text-[11px] text-muted-foreground">Oduzima se pri biranju dana godišnjeg odmora.</p>
            </div>
            <div className="space-y-1">
              <Label>Auto izračun iskorišteno (samo info)</Label>
              <Input type="number" value={form.iskoristeno_dana_auto ?? 0} disabled />
              <p className="text-[11px] text-muted-foreground">Zbroj odobrenih i izdanih GO zahtjeva.</p>
            </div>
            <div className="space-y-2 col-span-2 bg-secondary/40 rounded p-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="manual-override"
                  checked={!!form.iskoristeno_dana_manual_override}
                  onCheckedChange={(v) => setForm({...form, iskoristeno_dana_manual_override: !!v})}
                  data-testid="emp-manual-override"
                />
                <Label htmlFor="manual-override" className="cursor-pointer">Ručno preglasaj automatski izračun</Label>
              </div>
              <div className="space-y-1">
                <Label className={form.iskoristeno_dana_manual_override ? "" : "text-muted-foreground"}>
                  Iskorišteno dana (ručno)
                </Label>
                <Input type="number" min={0} value={form.iskoristeno_dana ?? 0}
                  onChange={e => setForm({...form, iskoristeno_dana: e.target.value})}
                  disabled={!form.iskoristeno_dana_manual_override}
                  data-testid="emp-iskoristeno"/>
                <p className="text-[11px] text-muted-foreground">Koristi se samo ako je gornja opcija uključena (npr. korekcija za zaposlenika koji je dio dana koristio prije uvođenja sustava).</p>
              </div>
            </div>
            {/* Iter45: prijenos iz prošle godine */}
            <div className="space-y-2 col-span-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded p-3">
              <Label className="text-xs uppercase tracking-wide text-amber-900 dark:text-amber-200">
                Prijenos iz {prevYear}. godine (vrijedi do 30.06.{prevYear + 1}.)
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Fond {prevYear}. (dana)</Label>
                  <Input type="number" min={0} max={MAX_DAYS_PER_YEAR}
                    value={form.prev_year_fond ?? ""}
                    placeholder="npr. 30"
                    onChange={e => setForm({...form, prev_year_fond: e.target.value === "" ? null : e.target.value})}
                    data-testid="emp-prev-fond"/>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Već iskorišteno iz {prevYear}.</Label>
                  <Input type="number" min={0}
                    value={form.prev_year_iskoristeno ?? ""}
                    placeholder="npr. 12"
                    onChange={e => setForm({...form, prev_year_iskoristeno: e.target.value === "" ? null : e.target.value})}
                    data-testid="emp-prev-iskoristeno"/>
                </div>
              </div>
              <p className="text-[11px] text-amber-900 dark:text-amber-200">
                Neiskorišteni prijenos iz prošle kalendarske godine. Dani su raspoloživi dok ne istekne 30.06.{prevYear + 1}.,
                nakon čega istječu i ne ulaze u izračun preostalog fonda.
              </p>
            </div>

            {/* Iter57e: oslobođen od kolektivnog godišnjeg */}
            <div className="space-y-2 border-t pt-3 col-span-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="exempt-from-collective"
                  checked={!!form.exempt_from_collective}
                  onCheckedChange={(v) => setForm({...form, exempt_from_collective: !!v})}
                  data-testid="emp-exempt-collective"/>
                <div className="space-y-1">
                  <Label htmlFor="exempt-from-collective" className="cursor-pointer">
                    Oslobođen od kolektivnog godišnjeg odmora
                  </Label>
                  <p className="text-[11px] text-muted-foreground">
                    Označi kad zaposlenik mora ostati u radu tijekom kolektivnog razdoblja (npr. znanstvenik na terenu, dežurni). Bulk-kolektivna izrada preskače ovog zaposlenika, a u svom zahtjevu mu se ne prikazuje obvezna kolektivna kartica.
                  </p>
                </div>
              </div>
            </div>

            {/* Iter91: voditelj može predavati zahtjeve u ime svog tima */}
            <div className="space-y-2 border-t pt-3 col-span-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="can-submit-for-team"
                  checked={!!form.can_submit_for_team}
                  onCheckedChange={(v) => setForm({...form, can_submit_for_team: !!v})}
                  data-testid="emp-can-submit-for-team"/>
                <div className="space-y-1">
                  <Label htmlFor="can-submit-for-team" className="cursor-pointer">
                    Može predavati zahtjeve u ime članova svog tima
                  </Label>
                  <p className="text-[11px] text-muted-foreground">
                    Namijenjeno voditeljima čiji članovi tima nemaju vlastiti pristup portalu (npr. tehničko osoblje). Kad je uključeno, ova osoba dobiva gumb <b>„Predaj u ime zaposlenika"</b> u sekciji Suglasnosti — smije predavati GO/RnD zahtjeve <b>isključivo</b> za zaposlenike kojima je označena kao voditelj (`manager_email`). Zahtjev preskače pending status: automatski dobiva suglasnost činom predaje i odlazi tajništvu na izradu rješenja.
                  </p>
                </div>
              </div>
            </div>
            {/* Iter92: mailovi dekanatu idu i glavnom tajniku */}
            <div className="space-y-2 border-t pt-3 col-span-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="forward-to-tajnik"
                  checked={!!form.forward_to_tajnik}
                  onCheckedChange={(v) => setForm({...form, forward_to_tajnik: !!v})}
                  data-testid="emp-forward-to-tajnik"/>
                <div className="space-y-1">
                  <Label htmlFor="forward-to-tajnik" className="cursor-pointer">
                    Proslijedi mailove dekanata glavnom tajniku
                  </Label>
                  <p className="text-[11px] text-muted-foreground">
                    Kad je uključeno, svi mailovi ovog zaposlenika koji su upućeni na <b>dekanat@intra.kif.unizg.hr</b> (obavijesti o novim zahtjevima, rješenja s PDF-om) automatski se šalju <b>i glavnom tajniku fakulteta</b>. Zaposlenik i dalje vidi da zahtjev ide dekanatu — prosljeđivanje je nevidljivo za njega.
                  </p>
                </div>
              </div>
            </div>
            {/* Iter103: per-user attendance override */}
            <div className="space-y-2 border-t pt-3 col-span-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="attendance-override-enabled"
                  checked={!!form.attendance_override_enabled}
                  onCheckedChange={(v) => setForm({...form, attendance_override_enabled: !!v})}
                  data-testid="emp-attendance-override"/>
                <div className="space-y-1">
                  <Label htmlFor="attendance-override-enabled" className="cursor-pointer">
                    Prikaži Prisutnost za ovog zaposlenika
                  </Label>
                  <p className="text-[11px] text-muted-foreground">
                    Kad je uključeno, zaposlenik uvijek vidi rubriku <b>Prisutnost</b> (RFID scan, ručna prijava/odjava, povijest) u navigaciji i na Dashboardu — <b>čak i kad je globalni prekidač &quot;Prisutnost svima&quot; isključen</b>. Neovisno od globalne postavke — služi kao whitelist za individualne zaposlenike.
                  </p>
                </div>
              </div>
            </div>
          </>}

          {isFullAdmin && (
            <div className="space-y-3 border-t pt-3 col-span-2">
              <Label className="text-xs uppercase text-muted-foreground tracking-wide">Evidencija prisutnosti</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>RFID kartica (UID)</Label>
                  <Input
                    value={form.rfid_card_uid || ""}
                    onChange={(e) => setForm({ ...form, rfid_card_uid: e.target.value })}
                    placeholder="npr. 04A1B2C3D4 — ili klikni Skeniraj"
                    autoComplete="off"
                    spellCheck={false}
                    data-testid="emp-rfid"
                  />
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <p className="text-[11px] text-muted-foreground flex-1">
                      UID kartice. Klikni <b>Skeniraj karticu</b> i prinesi novu karticu Pi čitaču u 30s.
                    </p>
                    <RfidScanButton
                      employeeEmail={form.email}
                      currentUid={form.rfid_card_uid}
                      onScanned={(uid) => setForm({ ...form, rfid_card_uid: uid })}
                      disabled={open === "new"}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!form.email || open === "new"}
                      onClick={async () => {
                        try {
                          await downloadAsBlob(
                            `/admin/rfid-pi/sticker/${encodeURIComponent(form.email)}?layout=single`,
                            `naljepnica-${(form.full_name || form.email).replace(/\s+/g, "_")}.pdf`,
                          );
                          toast.success("Naljepnica preuzeta");
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Greška");
                        }
                      }}
                      data-testid="emp-print-sticker"
                      title="PDF naljepnica s QR kodom za zaposlenika"
                    >
                      <Printer className="w-3.5 h-3.5 mr-1"/>Naljepnica
                    </Button>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Radni raspored</Label>
                  <Select
                    value={form.work_schedule_id || "__none__"}
                    onValueChange={(v) => setForm({ ...form, work_schedule_id: v === "__none__" ? "" : v })}
                  >
                    <SelectTrigger data-testid="emp-schedule"><SelectValue placeholder="Bez rasporeda"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Bez rasporeda —</SelectItem>
                      {workSchedules.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}{s.flexible ? " (fleksibilan)" : ` (${s.start_time || "?"}–${s.end_time || "?"})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">
                    Koristi se za izračun kašnjenja. Rasporedi se definiraju u Administracija → Radni rasporedi.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="px-6 py-4 border-t shrink-0 bg-background">
          <Button variant="outline" onClick={onClose}>Odustani</Button>
          <Button onClick={onSave} data-testid="save-employee-btn">Spremi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
